import { pantryColors, pantryTypography } from '@pantry/ui'
import React from 'react'
import { usePantryStore } from '../../../providers/PantryProvider'
import { useRelatedProduct } from '../hooks/useReview'
import { buildProductUrl } from '../utils/productUrl'

const p = pantryColors
const F = pantryTypography.fontFamily

interface RelatedProductCardProps {
  storeId: string
  productId: number
  productName: string
  productSlug: string
}

export const RelatedProductCard: React.FC<RelatedProductCardProps> = ({ storeId, productId, productName, productSlug }) => {
  const { data: product } = useRelatedProduct(storeId, productId)
  const store = usePantryStore()

  // "Edit" goes to the in-app pantry Product editor (existing route, not owned by
  // Reviews — mirrors the legacy Comment module's RelatedProduct link exactly:
  // frontend/apps/breadstack-crm/src/pages/Comment/Form/RelatedProduct/index.js:38).
  const editUrl = storeId && productId ? `/s/${storeId}/products/${productId}/edit` : ''
  // "View" goes to the storefront product page — same store_url + product_slug
  // composition used by the Reviews list (see MINE-0005 / ReviewsTable.tsx),
  // not the WooCommerce product record's own (unreliable) `permalink` field.
  const viewUrl = buildProductUrl(store?.storeUrl, productSlug)

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: p.gray[500], marginBottom: 8, fontFamily: F }}>Related Product</div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', border: `1px solid ${p.gray[200]}`, borderRadius: 8, padding: 10 }}>
        <img
          src={product?.image || '/icons/no_image.svg'}
          alt=""
          style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover', background: p.gray[50] }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: p.gray[800], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {product?.name || productName}
          </div>
          <div style={{ fontSize: 11, color: p.gray[400], marginBottom: 4 }}>ID: {productId}</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 14, fontSize: 11.5 }}>
            {editUrl ? (
              <a href={editUrl} target="_blank" rel="noopener noreferrer" style={actionLinkStyle}>
                <EditIcon />
                Edit
              </a>
            ) : null}
            {viewUrl ? (
              <a href={viewUrl} target="_blank" rel="noopener noreferrer" style={actionLinkStyle}>
                <ExternalLinkIcon />
                View
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

// Neutral gray matches the dialog's own secondary text/actions (labels,
// Cancel button) rather than the brand-green primary CTA — these are
// auxiliary navigation links, not the modal's primary action.
const actionLinkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  color: p.gray[600],
  fontWeight: 500,
  textDecoration: 'none',
}

const EditIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13, flexShrink: 0 }}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z" />
  </svg>
)

const ExternalLinkIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13, flexShrink: 0 }}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
)
